import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { generatePersonalizedOffers } from '../../lib/openai';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiGift, FiLoader, FiCopy, FiCheck } = FiIcons;

function PersonalizedOffers({ customer }) {
  const [offers, setOffers] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleHistory, setVehicleHistory] = useState('');
  const [copied, setCopied] = useState(false);

  const generateOffers = async () => {
    setIsLoading(true);
    try {
      // Create a simplified customer profile for the AI
      const customerProfile = {
        name: `${customer.firstName} ${customer.lastName}`,
        status: customer.status,
        interestedVehicle: customer.interestedVehicle,
        budget: customer.budget,
        tags: customer.tags || []
      };
      
      const result = await generatePersonalizedOffers(customerProfile, vehicleHistory);
      setOffers(result);
    } catch (error) {
      console.error('Error generating personalized offers:', error);
      setOffers('Failed to generate offers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(offers);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <SafeIcon icon={FiGift} className="w-5 h-5 mr-2 text-primary-500" />
        Personalized Special Offers
      </h3>
      <div className="space-y-4">
        <div className="bg-gray-50 p-3 rounded-lg mb-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <p className="text-gray-500">Customer:</p>
            <p className="font-medium">{customer.firstName} {customer.lastName}</p>
            <p className="text-gray-500">Interest:</p>
            <p className="font-medium">{customer.interestedVehicle || 'Not specified'}</p>
            <p className="text-gray-500">Budget:</p>
            <p className="font-medium">{customer.budget ? `$${customer.budget.toLocaleString()}` : 'Not specified'}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle History & Preferences (Optional)
          </label>
          <textarea
            value={vehicleHistory}
            onChange={(e) => setVehicleHistory(e.target.value)}
            placeholder="E.g., Previously owned a Honda Civic for 5 years. Interested in better fuel efficiency and safety features."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={3}
          />
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={generateOffers}
          disabled={isLoading}
          className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiGift} className="w-4 h-4" />
              <span>Generate Special Offers</span>
            </>
          )}
        </motion.button>

        {offers && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
            <pre className="whitespace-pre-wrap text-sm font-sans">{offers}</pre>
            <button
              onClick={copyToClipboard}
              className="absolute top-2 right-2 p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Copy to clipboard"
            >
              <SafeIcon
                icon={copied ? FiCheck : FiCopy}
                className={`w-4 h-4 ${copied ? 'text-green-500' : ''}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonalizedOffers;